/**
 * Dashboard SSO rollout gate (#4193; was FR-034, T012 Part B).
 *
 * The hybrid login (email gateway, then SSO or legacy) is shown when the Backend's public web
 * rollout control says so (`hybridLoginRollout.web`, #4192), OR when a tester turned on the hidden
 * per-browser override. The control is read once per page load by `HybridLoginRolloutSync`
 * (`components/HybridLoginRolloutSync.tsx`) through `insightt-graphql`'s `useHybridLoginRollout`,
 * which reports into the store below. It replaced a build-time setting in #4193.
 */

export const AUTH_V2_SSO_LOCAL_OVERRIDE_STORAGE_KEY = 'auth_v2_sso_local_override';

/** Reads whether the hidden per-browser local override is currently active. */
export function isAuthV2SsoLocalOverrideActive(): boolean {
  try {
    return window.localStorage.getItem(AUTH_V2_SSO_LOCAL_OVERRIDE_STORAGE_KEY) === 'true';
  } catch {
    // Storage access can throw (privacy mode, disabled storage); fail closed to "not active".
    return false;
  }
}

const rolloutListeners = new Set<() => void>();

/**
 * Subscribes to rollout changes in this page (override toggles and control status changes), so the
 * sign-in router can switch between the legacy page and the gateway without a reload. Returns the
 * unsubscribe function.
 */
export function subscribeToRollout(listener: () => void): () => void {
  rolloutListeners.add(listener);
  return () => {
    rolloutListeners.delete(listener);
  };
}

/** Activates or clears the hidden per-browser local override. */
export function setAuthV2SsoLocalOverrideActive(active: boolean): void {
  if (active) {
    writeLocalOverride(() => window.localStorage.setItem(AUTH_V2_SSO_LOCAL_OVERRIDE_STORAGE_KEY, 'true'));
  } else {
    writeLocalOverride(() => window.localStorage.removeItem(AUTH_V2_SSO_LOCAL_OVERRIDE_STORAGE_KEY));
  }
  notifyRolloutListeners();
}

function notifyRolloutListeners(): void {
  rolloutListeners.forEach((listener) => listener());
}

/** Runs one storage write; storage failures are ignored, so the override simply does not persist. */
function writeLocalOverride(write: () => void): void {
  try {
    write();
  } catch {
    // Storage access can throw (privacy mode, disabled storage).
  }
}

/** How long the sign-in page waits for the control before showing the legacy sign-in (research.md R2). */
export const HYBRID_LOGIN_ROLLOUT_WAIT_MS = 3000;
/** Request timeout: a `web: true` answer before it can still turn the gate on (FR-007). */
export const HYBRID_LOGIN_ROLLOUT_REQUEST_TIMEOUT_MS = 10000;

/**
 * The control for this page load (data-model.md): `idle` until `HybridLoginRolloutSync` starts it,
 * `pending` while waiting, then `on` or `off`. Only `on` turns the gate on.
 */
export type HybridLoginRolloutStatus = 'idle' | 'pending' | 'on' | 'off';

let rolloutStatus: HybridLoginRolloutStatus = 'idle';
let rolloutAnswered = false;
let rolloutWaitTimer: ReturnType<typeof setTimeout> | null = null;

function setRolloutStatus(next: HybridLoginRolloutStatus): void {
  if (rolloutStatus === next) return;
  rolloutStatus = next;
  notifyRolloutListeners();
}

/** The control's status for this page load. */
export function getHybridLoginRolloutStatus(): HybridLoginRolloutStatus {
  return rolloutStatus;
}

/** Moves `idle → pending` and starts the sign-in wait; later calls do nothing. */
export function beginHybridLoginRolloutWait(): void {
  if (rolloutStatus !== 'idle') return;
  setRolloutStatus('pending');
  rolloutWaitTimer = setTimeout(() => {
    rolloutWaitTimer = null;
    if (rolloutStatus === 'pending') setRolloutStatus('off');
  }, HYBRID_LOGIN_ROLLOUT_WAIT_MS);
}

/**
 * Applies one `useHybridLoginRollout` result. Loading results are ignored; the first settled result
 * decides the page load: `on` only for an error-free `web === true`, otherwise `off` (FR-004). An
 * answer arriving after the wait already read `off` can still turn it `on` (FR-007). Never logs the
 * value (FR-012).
 */
export function reportHybridLoginRollout(result: {
  readonly loading: boolean;
  readonly web?: unknown;
  readonly error?: unknown;
}): void {
  if (result.loading || rolloutAnswered) return;
  rolloutAnswered = true;
  if (rolloutWaitTimer) {
    clearTimeout(rolloutWaitTimer);
    rolloutWaitTimer = null;
  }
  setRolloutStatus(!result.error && result.web === true ? 'on' : 'off');
}

/** Test-only reset of the control for this page load. */
export function resetHybridLoginRolloutForTesting(): void {
  if (rolloutWaitTimer) clearTimeout(rolloutWaitTimer);
  rolloutWaitTimer = null;
  rolloutAnswered = false;
  rolloutStatus = 'idle';
}

/**
 * Resolves the rollout gate: the Backend web control is `on`, or this browser's tester override is
 * on. Off while the control is idle, pending, or off, so the gate-off path is the default.
 */
export function isAuthV2SsoRolloutActive(): boolean {
  return rolloutStatus === 'on' || isAuthV2SsoLocalOverrideActive();
}

let ssoPathActive = false;

/** Records that boot started the SSO client for this page (session restore or callback; #4193). */
export function markSsoPathActive(): void {
  ssoPathActive = true;
}

/**
 * True when the SSO path is in use on this page: the rollout gate is on, or boot started the SSO
 * client for an existing session or a returning sign-in. Authentication telemetry follows it
 * (#4193 FR-006), so a gate-off page with no SSO activity still sends nothing.
 */
export function isSsoPathActive(): boolean {
  return ssoPathActive || isAuthV2SsoRolloutActive();
}

/** Test-only reset for {@link markSsoPathActive}. */
export function resetSsoPathActiveForTesting(): void {
  ssoPathActive = false;
}
